import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faDownload, faTrash, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';

// Angebote sind serverseitig untypisiert (any) – hier defensiv nachgebildet (siehe UI-Spec §4.3).
interface ProjectOffer {
    id?: string;
    fileName?: string;
    contentType?: string;
    sizeBytes?: number;
    released?: boolean;
    releasedAt?: string;
    releasedBy?: string;
    createdAt?: string;
    createdBy?: string;
    offerNumber?: string;
    totalSum?: number;
    acceptedAt?: string;
    acceptedByName?: string;
}

const money = (v?: number) =>
    v == null ? '–' : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const dateShort = (v?: string) => (v ? new Date(v).toLocaleDateString('de-DE') : '');

export const OffersDialog: React.FC<{ projectId: string | null; projectName?: string; onClose: () => void }> = ({
    projectId,
    projectName,
    onClose,
}) => {
    const { api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const fileInput = useRef<HTMLInputElement>(null);
    const [offers, setOffers] = useState<ProjectOffer[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [offerNumber, setOfferNumber] = useState('');
    const [totalSum, setTotalSum] = useState('');

    const reload = useCallback(() => {
        if (!projectId) return;
        setLoading(true);
        api.calculator
            .projectOfferList(projectId)
            .then((r) => setOffers((r.data as unknown as ProjectOffer[]) ?? []))
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, projectId, apiError]);

    useEffect(() => {
        if (projectId) reload();
    }, [projectId, reload]);

    const upload = async (file: File) => {
        if (!projectId) return;
        setBusy(true);
        try {
            // offerNumber/totalSum werden als zusätzliche Form-Felder mitgeschickt (Backend akzeptiert sie).
            await api.calculator.projectOfferUpload(projectId, {
                file,
                offerNumber: offerNumber.trim() || undefined,
                totalSum: totalSum ? Number(totalSum) : undefined,
            } as unknown as { file?: File | null });
            enqueueSnackbar('Angebot hochgeladen.', { variant: 'success' });
            setOfferNumber('');
            setTotalSum('');
            reload();
        } catch (e) {
            apiError(e);
        } finally {
            setBusy(false);
        }
    };

    const setReleased = async (offerId: string, released: boolean) => {
        if (!projectId) return;
        try {
            await api.calculator.projectOfferSetReleased(projectId, offerId, { released });
            enqueueSnackbar(released ? 'Angebot freigegeben – Kunde wurde benachrichtigt.' : 'Freigabe zurückgenommen.', {
                variant: 'success',
            });
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    const download = async (o: ProjectOffer) => {
        if (!projectId || !o.id) return;
        try {
            const r = await api.calculator.projectOfferDownload(projectId, o.id, { format: 'blob' });
            const url = URL.createObjectURL(r.data as unknown as Blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = o.fileName || 'angebot.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            apiError(e);
        }
    };

    const remove = async (o: ProjectOffer) => {
        if (!projectId || !o.id) return;
        if (!window.confirm(`Angebot „${o.fileName}" löschen?`)) return;
        try {
            await api.calculator.projectOfferDelete(projectId, o.id);
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    return (
        <Dialog open={!!projectId} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Angebote{projectName ? `: ${projectName}` : ''}</DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }} useFlexGap>
                    <TextField size="small" label="Angebots-Nr." value={offerNumber} onChange={(e) => setOfferNumber(e.target.value)} />
                    <TextField
                        size="small"
                        type="number"
                        label="Summe (netto)"
                        value={totalSum}
                        onChange={(e) => setTotalSum(e.target.value)}
                        slotProps={{ htmlInput: { step: 0.01 } }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faUpload} />}
                        onClick={() => fileInput.current?.click()}
                        disabled={busy}
                    >
                        PDF hochladen
                    </Button>
                    <input
                        ref={fileInput}
                        type="file"
                        accept="application/pdf,.pdf"
                        hidden
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) upload(f);
                            e.target.value = '';
                        }}
                    />
                </Stack>

                {(loading || busy) && <LinearProgress sx={{ mb: 1 }} />}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Datei</TableCell>
                            <TableCell>Nr.</TableCell>
                            <TableCell align="right">Summe</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Annahme</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {offers.map((o) => (
                            <TableRow key={o.id}>
                                <TableCell>{o.fileName}</TableCell>
                                <TableCell>{o.offerNumber || '–'}</TableCell>
                                <TableCell align="right">{money(o.totalSum)}</TableCell>
                                <TableCell>
                                    {o.released ? (
                                        <Chip size="small" color="success" label="Freigegeben" />
                                    ) : (
                                        <Chip size="small" label="Entwurf" />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {o.acceptedAt ? (
                                        <Tooltip title={`${o.acceptedByName || ''} · ${dateShort(o.acceptedAt)}`}>
                                            <Box component="span" sx={{ color: 'success.main', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <FontAwesomeIcon icon={faCircleCheck} /> {dateShort(o.acceptedAt)} {o.acceptedByName}
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            –
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Herunterladen">
                                        <IconButton size="small" onClick={() => download(o)}>
                                            <FontAwesomeIcon icon={faDownload} />
                                        </IconButton>
                                    </Tooltip>
                                    {o.released ? (
                                        <Button size="small" onClick={() => o.id && setReleased(o.id, false)}>
                                            Zurücknehmen
                                        </Button>
                                    ) : (
                                        <Button size="small" variant="outlined" onClick={() => o.id && setReleased(o.id, true)}>
                                            Freigeben
                                        </Button>
                                    )}
                                    <Tooltip title="Löschen">
                                        <IconButton size="small" color="error" onClick={() => remove(o)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {offers.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Noch keine Angebote hochgeladen.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
